import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { Search, Add } from '@mui/icons-material';
import {
  Button, Tooltip, Chip, Alert, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Box
} from '@mui/material';
import OtherOptionModal from './OtherOptionModal';
import Pagination from '../../common/Pagination';

const OtherOptionIndex = () => {
  useSessionValidation();
  const { companyId, token, userId } = useAuth();
  const { logAudit } = useAuditLog();

  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [otherOptions, setOtherOptions] = useState([]);
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingVariant, setLoadingVariant] = useState('default');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const otherOptionApiEndpoint = 'OthersApi';
  const balanceSheetApiEndpoint = 'BalanceSheetHeadApi';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);

  const fetchOtherOptions = async () => {
    try {
      const response = await fetchData(otherOptionApiEndpoint, `/${companyId}`, token);
      const data = response.response_data || response || [];
      const uniqueData = data.filter((item, index, self) =>
        index === self.findIndex((t) => t.Id === item.Id)
      );
      setOtherOptions(uniqueData);
    } catch (error) {
      console.error('Error fetching other options:', error);
      setOtherOptions([]);
    }
  };

  const fetchBalanceSheetHeads = async () => {
    try {
      const response = await fetchData(balanceSheetApiEndpoint, `/${companyId}`, token);
      const data = response.response_data || response || [];
      setBalanceSheetHeads(data);
    } catch (error) {
      console.error('Error fetching balance sheet heads:', error);
      setBalanceSheetHeads([]);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Fetching other options...');
      setLoadingVariant('default');
      await Promise.allSettled([fetchOtherOptions(), fetchBalanceSheetHeads()]);
      setLoadingMessage('Data loaded successfully!');
      setLoadingVariant('success');
      setTimeout(() => setLoading(false), 800);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingMessage('Failed to load data!');
      setLoadingVariant('error');
      setError('Failed to fetch data. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const fetchSingleItem = async (id) => {
    try {
      const response = await fetchData(otherOptionApiEndpoint, `/GetById/${id}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching other option by ID:', error);
      return null;
    }
  };

  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    if ((action === 'view' || action === 'edit') && item?.Id) {
      setLoading(true);
      setLoadingMessage('Fetching details...');
      setLoadingVariant('default');
      const freshData = await fetchSingleItem(item.Id);
      if (freshData) {
        const master = freshData?.Master || freshData || {};
        const typeObj = freshData?.Capital || freshData?.Asset || freshData?.Others || freshData?.IncomeExpense || {};
        const openingBalances = freshData?.OpeningBalances || [];
        const unwrapped = {
          ...master,
          ...typeObj,
          OpeningBalances: openingBalances,
          MasterId: master.Id || 0,
          TypeId: typeObj.Id || 0,
        };
        setSelectedItem({ ...unwrapped, type: 'otherOption' });
        setFormData({ ...unwrapped, type: 'otherOption' });
      } else {
        setSelectedItem({ ...item, type: 'otherOption' });
        setFormData({ ...item, type: 'otherOption' } || {});
      }
      setLoading(false);
    } else {
      setSelectedItem({ type: 'otherOption' });
      setFormData({ type: 'otherOption' });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} other option...`);
      setLoadingVariant('default');

      let dataToSend = { ...formData, CompanyId: companyId };
      delete dataToSend.type;

      if (dataToSend.BalanceSheetHeadId) dataToSend.BalanceSheetHeadId = parseInt(dataToSend.BalanceSheetHeadId) || null;
      if (dataToSend.MasterTypeId) dataToSend.MasterTypeId = parseInt(dataToSend.MasterTypeId) || null;
      dataToSend.IsPayableOrReceivable = dataToSend.IsPayableOrReceivable ?? false;
      dataToSend.IsActive = dataToSend.IsActive ?? true;

      if (modalAction === 'create') {
        const vmPayload = {
          Master: {
            Id: 0,
            Name: dataToSend.Name,
            SearchableName: dataToSend.SearchableName,
            Description: dataToSend.Description,
            BalanceSheetHeadId: dataToSend.BalanceSheetHeadId,
            CompanyId: companyId,
            CreatedBy: userId,
            CreatedDate: new Date(),
          },
          OpeningBalances: dataToSend.OpeningBalances || [],
        };
        await postData(otherOptionApiEndpoint, '', vmPayload, token);
        setSuccess('Other option created successfully!');
        logAudit(AUDIT_ACTION.CREATE, otherOptionApiEndpoint, 0, null, { Name: formData.Name || '' });
      } else if (modalAction === 'edit') {
        const itemId = formData?.TypeId || formData?.Id || selectedItem?.TypeId || selectedItem?.Id;
        const masterId = formData?.MasterId || selectedItem?.MasterId || 0;
        if (!itemId) throw new Error('Item ID is missing');

        const vmPayload = {
          Master: {
            Id: masterId,
            Name: dataToSend.Name,
            SearchableName: dataToSend.SearchableName,
            Description: dataToSend.Description,
            BalanceSheetHeadId: dataToSend.BalanceSheetHeadId,
            CompanyId: companyId,
            ModifiedBy: userId,
            ModifiedDate: new Date(),
            CreatedBy: dataToSend.CreatedBy,
            CreatedDate: dataToSend.CreatedDate,
          },          
          OpeningBalances: dataToSend.OpeningBalances || [],
        };
        await updateData(otherOptionApiEndpoint, itemId, vmPayload, token);
        setSuccess('Other option updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, otherOptionApiEndpoint, itemId, { ...selectedItem }, { ...formData });
      }

      handleCloseModal();
      await fetchOtherOptions();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError('Failed to save other option. Please try again.');
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleDeleteClick = (item) => { setDeleteItem(item); setDeleteConfirmOpen(true); };
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteData(otherOptionApiEndpoint, deleteItem.Id, token);
      setOtherOptions(otherOptions.filter(i => i.Id !== deleteItem.Id));
      setSuccess('Other option deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, otherOptionApiEndpoint, deleteItem.Id, { Name: deleteItem.Name }, null);
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 800);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete other option. Please try again.');
      setDeleteConfirmOpen(false);
      setDeleteItem(null);
      setTimeout(() => setLoading(false), 1000);
    }
  };
  const handleDeleteCancel = () => { setDeleteConfirmOpen(false); setDeleteItem(null); };

  const getFilteredData = () => {
    let filtered = otherOptions;
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.SearchableName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterActive !== 'all') {
      filtered = filtered.filter(item => filterActive === 'active' ? item.IsActive : !item.IsActive);
    }
    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    return {
      data: filtered.slice(startIndex, startIndex + rowsPerPage),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (value) => { setRowsPerPage(Number(value)); setCurrentPage(1); };
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData();

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Romans' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase', color: 'black' }}>Others</h4>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
          <TextField fullWidth size="small" placeholder="Search by name, description..." value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#667eea' }, '&.Mui-focused fieldset': { borderColor: '#667eea' } } }}
          />
          <Tooltip title="Create a new other option" arrow>
            <Button variant="contained" onClick={() => handleOpenModal('create')}
              sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: { xs: '6px', sm: '4px 12px' }, borderRadius: '6px', fontSize: '12px', fontWeight: '700', boxShadow: '0 2px 8px rgba(102,126,234,0.4)', minHeight: '29px', minWidth: { xs: '29px', sm: 'auto' }, textTransform: 'none', letterSpacing: '0.5px', whiteSpace: 'nowrap', '& .button-text': { display: { xs: 'none', sm: 'inline' } } }}>
              <Add sx={{ fontSize: '16px' }} /><span className="button-text" style={{ marginLeft: '4px' }}>Create Other</span>
            </Button>
          </Tooltip>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Name', 'Balance Sheet Head', 'Opening Balance', 'Opening Date', 'P/R', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={h} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{balanceSheetHeads.find(bs => bs.Id === item.BalanceSheetHeadId)?.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{(item.OpeningBalance || 0).toLocaleString()}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.OpeningDate)}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsPayableOrReceivable ? 'P/R' : 'Regular'} color={item.IsPayableOrReceivable ? 'warning' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details" arrow><Button size="small" onClick={() => handleOpenModal('view', item)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button></Tooltip>
                        <Tooltip title="Edit Other Option" arrow><Button size="small" onClick={() => handleOpenModal('edit', item)} disabled={!item.IsActive || item.Id < 100} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button></Tooltip>
                        <Tooltip title="Delete Other Option" arrow><Button size="small" onClick={() => handleDeleteClick(item)} disabled={item.Id < 100} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button></Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} itemsPerPage={rowsPerPage} onItemsPerPageChange={handleRowsPerPageChange} totalItems={totalCount} />
      </div>

      <LogoLoader open={loading} message={loadingMessage} variant={loadingVariant} />
      <OtherOptionModal open={openModal} onClose={handleCloseModal} modalAction={modalAction} selectedItem={selectedItem} formData={formData} setFormData={setFormData} onSave={handleSave} balanceSheetHeads={balanceSheetHeads} />

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span style={{ fontSize: '24px' }}>⚠️</span><Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '16px' }}>Confirm Delete</Typography></Box></DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '14px', color: '#4a5568' }}>Are you sure you want to delete "{deleteItem?.Name}"?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '6px' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>
    </div>
  );
};

export default OtherOptionIndex;
